export type IIMaintenance = {
  start_time?: Date,
  end_time?: Date | null,
  status?: string,
  message?: string,
}

export type HealthCheck = {
  status: string,
  message: string,
  time: Date,
  sites: string,
  version: string,
  ii_maintenance: IIMaintenance | null,
  timezone: string
}


export type UserStep = {
    id: number,
    name: string,
    description: string,
    completed_at: Date,
  }
export type Installer = {
  id: number,
  name: string,
  email: string,
  phone: string | null,
  logo: string,
  problem_report_email: string,
  licence_number: string | null,
  created_at: string,
}

// Contact-only slice of the site's installer, from
// /api/site_installer/{site_id} — the site-member-readable endpoint
// behind the readiness chain's "ask your installer" affordances. The
// email is the installer's problem-report address when one is set.
export type SiteInstallerContact = {
  id?: number,
  name?: string | null,
  email?: string | null,
  phone?: string | null,
  saa_accreditation?: string | null,
}

// Accredited installation team from the site's Formbay CER record, and
// whether the site's linked installer matches it. The education point:
// the SAA-accredited installer/designer who signed the job is often not
// the company the customer actually deals with (a retailer or call
// centre may front for them). installer_matches_team is null when there
// is nothing to compare.
export type FormbayAccreditation = {
  has_record: boolean,
  company_name?: string | null,
  team: {
    role: string,
    name?: string | null,
    saa_code?: string | null,
  }[],
  installer_matches_team?: boolean | null,
}

export type OnboardRequest = {
  id: number;
  nmi: string;
  inverter_serial: string;
  controller_serial: string;
  installer_id: number;
  status: 'pending' | 'completed' | 'failed' | 'in_progress';
  details: Record<string, any>;
  created_at: string;
  site_id: number | null;
  completed_at: string | null;
}

export type CouponCode = {
  id: number;
  discount: number | null;
  deleted_at: Date | null;
  code: string;
  code_set: string;
  user_id: number;
  created_at: Date | string;
};
export type User = {
    site_creation?: { any: boolean; installer_ids: number[] },
    id: number,
    name: string,
    discord_user_id?: string,
    role?: string,
    installer_id?: number,
    installer_logo?: string,
    email: string,
    coupon_codes?: CouponCode[],
    exp?: number,
    phone?: string,
    shipping_address_line1?: string,
    shipping_address_line2?: string,
    shipping_city?: string,
    shipping_state?: string,
    shipping_zip?: string,
    // Distinct from `country`: this is the label's destination country, sent
    // to MailPlus with the rest of the shipping address.
    shipping_country?: string,
    country?: string,
    image?: string,
    total_steps?: number,
    completed_steps?: number,
    completed?: boolean,
    subscription_status?: string,
    // Chargebee IDs — exposed to admins so they can deep-link into the
    // Chargebee dashboard for billing / subscription ops.
    chargebee_subscription_id?: string,
    chargebee_customer_id?: string,
    shipping_tracking_url?: string,
    shipping_mac_address?: string,
    mqtt_username?: string,
    mqtt_password?: string,
    lv_nmi?: string,
    cb_nmi?: string,
    cb_brand?: string,
    // Captured at signup time. All fields optional so partial / legacy
    // records render without breaking. Surfaced to admins / installers on
    // the /users sidebar so they can see how each customer onboarded
    // without digging into the database.
    signup_meta?: {
      brand?: string;
      nmi?: string;
      inverter_serial?: string;
      inverter_kw?: number | null;
      battery_kwh?: number;
      phase?: 'single' | 'three';
      system_classification?: 'trading-ready' | 'limited' | 'unknown';
      three_phase_underpowered?: boolean;
      decision?: 'proceed' | 'upgrade' | 'exit';
      switchboard_confirmed?: boolean;
      system_acknowledged?: boolean;
      trading_readiness?: {
        importExport?: boolean;
        meterCtAligned?: boolean;
        configuration?: boolean;
        dnspSettings?: boolean;
        requestEvaluation?: boolean;
      };
      photos?: {
        nameplate?: boolean | { bucket?: string; key?: string };
        serial?: boolean | { bucket?: string; key?: string };
        switchboard?: boolean | { bucket?: string; key?: string };
        bill?: boolean | { bucket?: string; key?: string };
        bill_skipped?: boolean;
      };
      signed_up_at?: string; // ISO timestamp
    },
    // Raw signup metadata blob as stored on the user row. Backend may
    // return this as a JSON string or already-parsed object — admins see
    // it verbatim in the /users sidebar for debugging.
    meta_data?: string | Record<string, unknown> | null,
  }
  export type ApiKey = {
    id: number,
    user_id: number,
    key: string,
    description: string,
    created_at: Date,
    expires_at: Date,
  }

export type RegisterMessage = {
  'registry': string;
  'value': number;
}

  export type InverterControlMessage = {
    action: string,
    settings: Array<RegisterMessage>;
  }
  export type ControlMessage = {
    inverter: InverterControlMessage,
    reason?: string,
    packet_version?: number;
    interval_time: Date,
    app_version: string
  }

  export type CustomSettings = {
    inverter: {
      action: string,
      reason?: string,
      settings: Array<{registry: string, value: number}>
    }
  }

  export type CustomInverterSettings = {
    id?: number,
    mode: string,
    settings: CustomSettings,
  }

  export type InverterSettings = {
    id: number,
    inverter_id: number,
    mode: string,
    settings: CustomSettings,
  }

  export interface HourlyRate {
    hour: number;
    expected_bill: number;
    hourly_costs: number;
    total_costs: number;
    rate_costs: number;
    total_energy_imported: number;
    buy_rate: number;
    total_battery_kwh: number;
    total_energy_exported: number;
    total_earnings: number;
    sell_rate: number;
    daily_costs: number;
  }

  export const site_edit_fields = [
      ["name", "The internal name for the site (no spaces)", []],
      ["tariff", "* The tariff for the site", []],
      ["network", "* The network for the site", ["energex", "ergon", "ausgrid", "endeavour", "essential", "evoenergy", "sapn", "tasnetworks", "ausnet", "powercor", "united", "other"]],
      ["nmi", "The NMI for the site", []],
      ["connection_type", "The connection type for the site", ["single_phase", "three_phase"]],
      ["address", "The address for the site", []],
      ["brand", "The inverter brand for the site", ["Sonnen", "SUNGROW", "mixed", "FoxESS", "LG", "Fronius", "SolaX", "SIGENERGY", "AlphaESS", "SAJ", "DEYE", "Other"]],
      ["address_type", "The address type for the site", ["residential", "commercial"]],
      ["owner_name", "The owner's name", []],
      ["owner_email", "The owner's email", []],
     ];

  export type Site = {
    id: number,
    name: string,
    display_name: string,
    nmi: string,
    tariff: string,
    network: string,
    address: string,
    location: string,
    latitude: string
    longitude: string,
    ev_charge_limit: number,
    pvoutput_site_id: number,
    pvoutput_api_key: string,
    active_retailer: string,
    amber_site_id?: string | null,
    owner_user_id?: number | null,
    emporiaenergy_gid: string,
    charger_id: string,
    retail_yesterday_buy_rate: number,
    retail_yesterday_sell_rate: number,
    ha_publish: string,
    mqtt_username: string,
    mqtt_password: string,
    hide_all: boolean,
    read_only: boolean,
    chargebee_subscription_id: string
    subscription_status: string
    primary_inverter_id: number,
    /** Backend #2236: present only when the partner's API trial has ended. */
    api_trial_ended_notice?: { state: 'api_trial_ended'; message: string } | null,
    // The site's main billing/utility meter — pass straight to
    // /api/meter_data/{site_id}/{meterId}. Backfilled by summary_table_update
    // (NMI match, else most-active meter); NULL until that job runs once for
    // a given site, so callers should fall back to utility_meters[0].
    primary_utility_meter_id?: number | null,
    installed_solar: number,
    installed_battery: number,
    inverter_power: number,
    owner_name: string,
    owner_email: string,
    brand: string,
    anonymous_name?: string,
    installer_id?: number,
    state?: string,
    // IANA timezone of the physical site (e.g. "Australia/Brisbane"). The
    // summary page uses this — not the viewer's browser clock — to decide
    // where each "day" begins (see app/lib/siteDay.ts).
    timezone?: string,
    postcode: string,
    install_cost?: number,
    // ISO datetime string for when this site record was first created.
    // Used by the ROI math as the start of the lifetime savings window
    // (see doc/roi-math.md §1). Optional because some legacy sites
    // don't have it populated yet — callers should fall back to a
    // 12-month window in that case.
    created_at?: string,
    api_errors?: Record<string, string>,
    // Whether the caller may take the site off Powston — the same check as
    // DELETE /api/site/{site_id} and the unbind routes (backend #2207).
    // Absent from older backends; treat anything but true as "no".
    can_release_site?: boolean,
    utility_meters?: UtilityMeter[],
    // --- Support Pathway (site-level overrides; the resolved owner comes
    // from GET /api/site/{site_id}/support_route) ---
    support_route?: SupportRoute | null,
    support_organisation_name?: string | null,
    support_email?: string | null,
    support_phone?: string | null,
    support_portal_url?: string | null,
    installer_organisation_name?: string | null,
    system_supplier_name?: string | null,
    powston_escalation_enabled?: boolean | null,
    support_policy_version?: string | null,
    support_route_confirmed_at?: string | null,
    [key: string]: any;
  }

  // Who provides first-line support for a site (Support Pathway).
  export type SupportRoute =
    | 'powston_installed'   // Powston designed/installed — contact Powston
    | 'partner_managed'     // named partner (e.g. REA Global) is first-line
    | 'self_service'        // Powston Assistant / app problem report
    | 'control_only'        // installer/supplier first; Powston provides evidence
    | 'unassigned';         // not yet confirmed — flagged for migration

  // Resolved support owner from GET /api/site/{site_id}/support_route.
  export type SupportRouteInfo = {
    site_id: number,
    route: SupportRoute,
    organisation_name: string,
    email: string,
    phone?: string | null,
    portal_url?: string | null,
    powston_escalation_enabled: boolean,
    source: 'site' | 'installer' | 'default',
    confirmed_at?: string | null,
    policy_version?: string | null,
    installer_organisation_name?: string | null,
    system_supplier_name?: string | null,
    acknowledgement_text: string,
    acknowledged: boolean,
  }

  export type SupportCaseEvent = {
    id?: number,
    event_type: 'intake' | 'delivery' | 'triage' | 'source_bound' |
      'status_changed' | 'acknowledged' | 'reopened' | 'owner_changed' | string,
    source_channel?: string | null,
    actor_user_id?: number | null,
    actor_name?: string | null,
    previous_value?: Record<string, unknown> | null,
    new_value?: Record<string, unknown> | null,
    reason?: string | null,
    details?: Record<string, unknown> | null,
    occurred_at: string,
  }

  // The complete customer-visible lifecycle vocabulary shared by the support
  // case UI. Keep this aligned with REPORT_STATUSES + CASE_STATES in the API;
  // unknown future values are still accepted so the UI can fail honestly.
  export const SUPPORT_CASE_STATUSES = [
    'sent_to_partner',
    'sent_to_powston',
    'escalated_to_powston',
    'awaiting_customer_confirmation',
    'acknowledged',
    'resolved',
    'send_failed',
    'powston_responsibility_accepted',
    'awaiting_installer',
    'awaiting_manufacturer',
    'awaiting_retailer',
    'awaiting_self_managed_action',
    'awaiting_customer_action',
    'awaiting_owner_nomination',
    'evidence_required',
    'classification_inconclusive',
    'closed_without_action',
  ] as const;

  export type SupportCaseStatus = typeof SUPPORT_CASE_STATUSES[number];

  // Diagnostic context is returned only to authorised installer/admin viewers.
  // The public case list deliberately omits this object server-side; the UI
  // also fails closed and never renders it for a customer role.
  export type SupportCaseContext = {
    site?: {
      id?: number,
      display_name?: string | null,
      authorised_identity?: string | null,
    } | null,
    telemetry?: {
      freshest_at?: string | null,
      age_minutes?: number | null,
      status?: string | null,
    } | null,
    recent_commands?: Array<Record<string, unknown>> | null,
    recent_errors?: Array<Record<string, unknown>> | null,
    browser?: Record<string, unknown> | null,
    url?: string | null,
    screenshot_attached?: boolean,
    diagnostic_retention_until?: string | null,
  }

  // A row from GET /api/support_reports/{site_id} — customer-visible status.
  export type SupportReportStatus = {
    id: number,
    reference?: string | null,
    created_at: string,
    status: SupportCaseStatus | (string & {}),
    support_route: SupportRoute,
    support_organisation_name: string,
    category?: string | null,
    is_safety: boolean,
    severity?: 'normal' | 'critical' | string | null,
    case_disposition?: 'site_not_trading_ready' | 'expired_unactioned' | string | null,
    support_owner_user_id?: number | null,
    support_owner_name?: string | null,
    acknowledged_at?: string | null,
    updated_at?: string | null,
    resolved_at?: string | null,
    reopened_at?: string | null,
    // Primary fault domain from the routing ladder (null = unresolved, see
    // fault_domain_candidates): safety_event | control_decision |
    // control_delivery | control_mismatch | equipment_internal | workmanship |
    // design | metering_retailer | network.
    fault_domain?: string | null,
    fault_domain_candidates?: string | null,
    interval_time?: string | null,
    commanded_action?: string | null,
    reported_response?: string | null,
    response_classification?: string | null,
    escalation_category?: string | null,
    escalated_at?: string | null,
    events?: SupportCaseEvent[] | null,
    context?: SupportCaseContext | null,
  }

  export type SiteStatus = {
    id: number
    buy_price: number,
    sell_price: number,
    rrp: number,
    next_rrp: number,
    action: string,
    soc: number,
  }

  export type Stats = {
    battery_count: number,
    battery_capacity: number,
    battery_soc: number,
    energy_stored: number,
  }

  type WeatherData = {
    latitude: number,
    longitude: number,
    generationtime_ms: number,
    utc_offset_seconds: number,
    timezone: string,
    timezone_abbreviation: string,
    elevation:  number,
    hourly: {
      time: string[]
      temperature_2m: number[],
      cloud_cover: number[],
      global_tilted_irradiance_instant: number[],
      direct_radiation: number[],
      uv_index: number[]
    },
  }

  type SiteTotals = {
      exportsAll: number,
      importsAll: number,
      demandMain: number,
      demandPeriod: number,
      demandInterval: number,
      earningsAll: number,
      earningsAllVar: number,
      earningsAllFixed: number,
      earningsFlexUp: number,
      earningsFlexDown: number,
      costsAll: number,
      costsAllVar: number,
      costsAllFixed: number,
      costsDemandMain: number,
      costsDemandRate: number,
      costsFlexUp: number,
      costsFlexDown: number,
      exportsAllEmissions: number,
      importsAllEmissions: number,
      exportsAllZeroEE: number,
      buy_rate?: number,
      sell_rate?: number,
      expected_bill?: number,
    }
  type SiteStatistics = {
    site_id: number,
    site_name: string,
    nmi: string,
    start_date: Date,
    end_date: Date,
    yesterday: SiteTotals,
    weekly: SiteTotals,
    records: number,
    totals: SiteTotals
  }
  type InverterTotals = {
      e_grid: number,
      energy_import: number,
      energy_export: number,
      earnings: number,
      costs: number,
    }
  type InverterStatistics = {
    inverter_id: number,
    totals: InverterTotals
  }

  export type CodeParams = {
    interval_time: Date,
    cached_time?: Date,
    state: string,
    solar_power: number,
    grid_power: number,
    house_power: number,
    battery_power: number,
    battery_current?: number,
    rrp: number,
    forecast: number[],
    buy_price: number,
    buy_forecast: number[],
    sell_price: number,
    sell_forecast: number[],
    battery_capacity: number,
    battery_soc: number,
    timezone_str: string,
    optimal_charging: number,
    optimal_discharging: number,
    feed_in_power_limitation?: number,
    last_action: string,
    suggested_action: string,
    suggested_solar: string,
    serial_number: string,
    product_name: string,
    work_mode_label: string,
    system_state_label: string,
    site_name: string,
    inverter_id: number,
    powston_managed: boolean,
    site_id: number,
    hybrid: boolean,
    data_logger: boolean,
    display_only?: boolean,
    // Set when the inverter has been permanently replaced and retired to the
    // recycled site. Such inverters are hidden from the UI.
    replaced_at?: string | null,
    use_api: boolean
    use_local: boolean,
    cloud_cover?: number,
    weather_data?: WeatherData,
    manufacturer: string,
    // Set when the iSolarCloud sweep has matched this inverter — its power
    // ratings are then vendor-synced and read-only in the UI.
    vendor_key?: string | null,
    api_only?: boolean,
    read_only: boolean,
    lv_buy_price?: number,
    lv_sell_price?: number,
    action?: string,
    reason?: string,
    // Customer-facing reason generated on the backend (mirrors decisions.user_reason).
    user_reason?: string,
    solar?: string,
    runtime_params?: {
      battery_soc?: number; grid_power?: number; house_power?: number; battery_power?: number; solar_power?: number;
      // Sigenergy DC EV charger telemetry (Modbus EV block, surfaced through
      // runtime_cache.ACTION_FIELD_MAPPING). Absent on every other charger:
      // the Sigen AC charger and the Sungrow iHomeManager expose no power
      // register through this payload, so a missing value means "not
      // measured here", never 0 W. Some firmware reports the charging draw
      // negative — see app/lib/evChargerTile.ts for the sign rule.
      dc_charger_power?: number | null;
      // iHM/Sigen running-state enum: 0 Idle, 1 Occupied, 2 Preparing,
      // 3 Charging, 4 Fault, 5 Scheduled, 6 Ended, 7 Unavailable,
      // 8 Discharging, 9 Alarm, 10 Preparing (insulation check).
      ev_dc_running_state?: number | string | null;
      ev_dc_running_state_label?: string | null;
    },
    // --- EV charger (site-level facts the decision run attaches; see
    // inverter-intelligence decision_variables.py) ---
    // True when the site has real evidence of an EV charger: a car_chargers
    // row, OCPP MeterValues, or a Sigen DC/AC charger. Missing on payloads
    // that predate the field — treat as "no charger known".
    has_ev_charger?: boolean,
    has_ac_charger?: boolean,
    has_dc_charger?: boolean,
    // True when Powston can command the charger (OCPP / Modbus / native Sigen
    // path opted in), as opposed to only knowing it exists.
    ev_night_managed?: boolean,
    // Whether the current interval is one Powston has opened for charging:
    // overnight (cheapest remaining slots for the budget) or daytime (solar /
    // low-price top-up). These are PERMISSIONS the decision issued, not a
    // measurement that the car is drawing.
    ev_night_grant?: boolean,
    ev_daytime_grant?: boolean,
    ev_night_window?: boolean,
    ev_daytime_window?: boolean,
    // The owner's daily EV energy budget (kWh) and, where the charger's
    // delivery is measurable, what it has delivered so far this budget period.
    ev_budget_kwh?: number | null,
    ev_budget_kwh_delivered?: number | null,
    site_statistics?: SiteStatistics,
    inverter_statistics?: InverterStatistics,
    decisions?: Decisions,
    min_sell_rrp?: number,
    projected_deficit?: number,
    night_reserve?: number,
    feed_in_tariff?: number,
    tariff?: string,
    export_tariff?: string,
    history_rrp?: number[],
    history_buy_prices?: number[],
    history_sell_prices?: number[],
    // Authoritative timestamp per history sample, parallel to the arrays
    // above. The series is not reliably contiguous — live payloads skip
    // intervals — so labels must read this rather than assume a five-minute
    // walk back from interval_time.
    history_interval_time?: string[],
    is_non_conforming_flag?: boolean[],
    api_errors?: string,
    network?: string,
    market?: number,
    last_inverter_time?: Date,
    is_pending?: boolean,
    manual_action_id?: number | null;
  }

  export type CodeParamsQueryResponse = {
    total: number,
    total_is_lower_bound?: boolean,
    has_more?: boolean,
    limit: number,
    offset: number,
    items: CodeParams[]
  }

export interface OrderFormData {
  // Installer Information
  company_name?: string;
  installer_contact?: string;
  phone?: string;
  email?: string;
  purchase_order?: string;
  // Order Volume
  product_type?: 'residential' | 'commercial';

  // End Customer
  customer_name?: string;
  customer_address?: string;
  customer_phone?: string;
  customer_email?: string;
  nmi?: string;

  // Deployed Infrastructure
  serial_number?: string;
  inverters?: string;
  batteries?: string;
  solar_panels?: string;

  // Bill To
  bill_to_company_name?: string;
  bill_to_abn?: string;
  bill_to_address?: string;
  bill_to_email?: string;
  bill_to_phone?: string;
  // Ship To
  shipping_destination?: 'customer' | 'other';
  shipping_address?: string;
  shipping_contact_name?: string;
  shipping_mobile?: string;

  // Additional
  notes?: string;

  // Post order status fields
  order_status?: string;
  shipping_tracking_url?: string;
}

  export type UserCode = {
    id: number
    inverter_id: number
    tests_passed: boolean
    name: string
    user_code: string
    tests_run_at: Date
    test_results: string
    enabled: boolean
    log_filename: string
    simulation_results: string
  }

  export type Decision = {
    reason: string,
    current: string,
    wanted: string,
    priority: number,
    applied: boolean | null,
    values: Record<string, any>,
    user_reason?: string
  }

  export type Decisions = {
    decisions: Decision[],
    current_action: string,
    current_priority: number,
    log: string,
    // Customer-facing reason generated on the backend (decision_logger.get_user_reason()).
    user_reason?: string
  }
  
  export type Inverter = {
    id: number,
    site_id: number,
    site_name: string,
    ip_address: string,
    product_name: string,
    installed_panels: string,
    hybrid: boolean,
    data_logger: boolean,
    display_only: boolean,
    max_ppv_power: number,
    battery_capacity: number,
    serial_number: string,
    action: string,
    time: string,
    updated_at: string,
    reason: string,
    minutes_ago: number,
    day_cost: number,
    pgrid: number,
    ppv: number,
    three_day_cost: number,
    battery_soc: number,
    pbattery1: number,
    retail_yesterday_cost: number,
    retail_yesterday_earnings: number,
    retail_yesterday_buy_usage: number,
    retail_yesterday_buy: number,
    retail_yesterday_buy_rate: number,
    retail_yesterday_sell_rate: number,
    week_cost: number,
    use_api: boolean,
    use_local: boolean,
    api_only: boolean,
    retail_yesterday_cost_png: string,
    estimated_yesterday_bill: number,
    retail_vs_estimated: number,
    dongle_serial_number: string,
    grid_power_setting: string | null,
    battery_power_setting: string | null,
    solar_power_setting: string | null,
    house_power_setting: string | null,
    manufacturer: string | null,
    panel_wattage: number | null,
    installed_pv_power: number | null,
    pv_strings: number | null,
    battery_voltage: string | null,
    coupling_type: string | null,
    battery_model: string | null,
    timezone: string,
    // Set when the inverter has been permanently replaced and retired to the
    // recycled site. Such inverters are hidden from the UI. Optional, so the index
    // signature below includes `undefined` to stay compatible (TS2411) without
    // forcing every Inverter literal/fixture to provide it (TS2741).
    replaced_at?: string | null,
    [key: string]: number | string | boolean | null | undefined;
  };

export type InverterQueryResult = {
  total: number;
  limit: number;
  offset: number;
  items: Inverter[];
}

export interface RuntimeData {
    [key: string]: number | string;
  }
  
  export type PriceItem = {
    'date': Date;
    'rrp': number;
    'general_tariff': number;
    'feed_in_tariff': number;
    'start_soc'?: number;
    'forecasts'?: number[];
    'e_grid'?: number;
    'quality'?: string;
  }

  export type PriceData = {
      'rrp': PriceItem;
      'rrps': PriceItem[];
      'forecasts': PriceItem[];
      'combined': PriceItem[];
      'dlf': number | null;
      'interval_time': string;
      'network': string;
      'nmi': string;
      'site_id': number;
      'state': string;
      'tariff': string;
      'timezone': string;
  }

  export type EvMeterData = {
    'timestamp': Date;
    'voltage': number;
    'current_import': number;
    'temperature': number;
    'day_energy': number;
    'day_cost': number;
  }

  export type SiteSpecs = {
    installed_solar: number;
    installed_battery: number;
    inverter_power: number;
    wind_power?: number;
    hydro_power?: number;
  }

  export type RateData = {
    site_id: number;
    state: string;
    tariff: string;
    network: string;
    brand: string;
    inverter_power: number;
    record_count: number;
    /** Settled rows with positive consumption; absent on older API payloads. */
    consumption_days?: number;
    expected_bill: number;
    daily_costs: number;
    total_costs: number;
    total_energy_imported: number;
    rate_costs: number;
    buy_rate: number;
    total_energy_exported: number;
    total_energy_consumption?: number;
    total_earnings: number;
    rate_earnings: number;
    sell_rate: number;
    // LocalVolts v2 P2P sales split (export side), in CENTS like the fields above.
    // null = not enriched / non-LocalVolts site. total with P2P = spot + matched;
    // total if all spot = that - benefit; win/loss = benefit (+ = P2P won).
    p2p_sales_spot?: number | null;      // cents: sales that settled at wholesale spot
    p2p_sales_matched?: number | null;   // cents: sales that settled via a P2P match
    p2p_sales_benefit?: number | null;   // cents: uplift vs valuing the matched volume at spot
    total_p2p_matched_kwh?: number | null;  // kWh of export matched P2P
    p2p_proportion?: number | null;      // 0..1 share of export volume matched
    start_time: Date;
    end_time: Date;
    // Set for non-staff callers: the backend flags the requesting user's own
    // sites so the UI can offer a "mine only" view without another request.
    is_own_site?: boolean;
    eligible?: boolean;
    ineligible_reason?: string;
  } & SiteSpecs;
  
  // One premise record from a DNSP EPP capture (see dnsp_info below).
  export type DnspPremiseRecord = {
    Frmp?: string;
    Nmi?: string;
    updated_at?: string;
    [key: string]: unknown;
  };

  export type OnboardingChecks = {
    nmi: boolean;
    nmi_reporting: boolean;
    nmi_actual: boolean;
    has_site_monitor: boolean;
    site_monitor_configured: boolean;
    // Optional: the backend omits this for sites that don't need a
    // local Powston controller (manufacturer gateway sites like
    // Logger1000 / iHomeManager, or any site type without a controller
    // requirement). When undefined, controller-dependent checks
    // (recent_data, recent_modbus_data, etc.) should be treated as
    // not-applicable rather than failing.
    site_monitor_online?: boolean;
    site_monitor_local?: boolean;
    site_monitor_time: number;
    site_monitor_inverter: boolean;
    has_inverter: boolean;
    inverter_controlled: boolean;
    tariffs: boolean;
    // Populated only when `tariffs` is false: per-side breakdown of why the
    // site's retail tariffs aren't the ones Powston trades best on. Lets the
    // UI tell the customer which side (import / export) to ask their retailer
    // to change. Null when the backend has no breakdown (older sites / no
    // detail). `*_non_conforming`: true = that side needs changing, false =
    // fine, null = unknown / not assessed. NOTE: the raw tariff codes here are
    // for internal logic only — Powston's preferred-tariff selection is
    // "secret sauce" and the codes must never be shown to customers (see
    // MarketCard). Tariff selection is a RETAILER action, not an installer one.
    tariff_debug?: {
      import_non_conforming?: boolean | null;
      export_non_conforming?: boolean | null;
      site_tariff?: string | null;
      site_export_tariff?: string | null;
      site_network?: string | null;
      meter_lnsp?: string | null;
      meter_import_tariff?: string | null;
      meter_export_tariff?: string | null;
      // Retailer the site imports from, lower-cased (e.g. "amber",
      // "localvolts"). Mirrors Site.active_retailer.
      active_retailer?: string | null;
      // Powston's single preferred tariff code for each side, and the full
      // list of acceptable (conforming) codes. The backend only includes
      // these when it's appropriate to reveal the code to the customer (e.g.
      // retailers like Amber where the customer can request a specific
      // tariff) — so the UI names the code whenever it's present and falls
      // back to generic "preferred tariff" wording when it's absent. The
      // codes are otherwise Powston's preferred-tariff "secret sauce".
      preferred_import_tariff?: string | null;
      preferred_export_tariff?: string | null;
      preferred_import_tariffs?: string[] | null;
      preferred_export_tariffs?: string[] | null;
    } | null;
    // Server-rendered, retailer-aware tariff recommendation (e.g. "Ask Amber to
    // apply our preferred tariff PRDS."). NOTE: the UI deliberately builds its
    // own ask via lib/tariffs.preferredTariffAsk instead of rendering this — the
    // client version adds the NSW Solar Bonus export guard (never tells a 9900 /
    // GEN2028 site to switch off its premium feed-in), which the server hint
    // doesn't. Kept here so the field is typed, not unknown.
    tariff_hint?: string;
    user_code: boolean;
    user_code_enabled: boolean;
    all_reporting?: boolean;
    recent_data: boolean;
    recent_modbus_data: boolean;
    inverter_battery_capacity: number;
    inverter_serial_numbers?: string[];
    // Site configuration
    site_location?: boolean;
    site_timezone?: boolean;
    owner_set?: boolean;
    battery_capacity_set?: boolean;
    // Network correctness
    network_valid?: boolean;
    // Rule execution
    recent_action?: boolean;
    // Meter data freshness
    nmi_recent?: boolean;
    // Set only when nmi_actual is false. True means the meter feed is
    // alive but delivering only estimates (Exp/Fcst) with no actuals —
    // either the expected retailer isn't the FRMP yet, or the premise
    // has an accumulation (type 6) meter that cannot report interval
    // data and needs a type 4 smart meter. False means nothing recent
    // is arriving at all (dead feed).
    nmi_estimates_only?: boolean;
    // DNSP address verification: does the utility meter's DNSP premise
    // address match the site? false = mismatch (an affected site).
    // Persisted / job-refreshed (NOT live-geocoded per request) so it can be
    // returned in the bulk installer_all_sites payload. Omitted for sites with
    // no utility meter to verify, in which case it is treated as N/A.
    dnsp_address_match?: boolean;
    // Raw DNSP premise payload (Energex/Ergon EPP capture) stored on the
    // site's utility meter. The backend includes it only while fresh —
    // captured within the last 7 days. Usually a list of premise records;
    // the first carries Frmp, the AEMO participant id of the retailer
    // currently responsible for the NMI.
    dnsp_info?: DnspPremiseRecord[] | DnspPremiseRecord | null;
    // Energex sites with a fresh DNSP capture only: false when the premise
    // record has no ExistingEGCapacity, i.e. the network has no record of
    // the embedded generation — the installer needs to lodge/complete the
    // EG connection application. Omitted for non-Energex networks or when
    // no fresh capture exists.
    dnsp_eg_registered?: boolean;
    // Firmware
    saj_aio3_powston_firmware?: boolean;
    // SAJ has no remote-control API path. When set to false, the inverter
    // has been flipped to use_api=True by check_saj_modbus (because modbus
    // packets stopped landing in runtime_data) — control is lost until the
    // site monitor / AIO3 dongle is investigated.
    saj_local_active?: boolean;
    // SolaX cloud-API sites authenticate to the SolaX VPP API with a per-user
    // OAuth token that expires ~daily and is auto-refreshed by the backend.
    // false = the token expired AND auto-refresh failed, so telemetry/control
    // stopped until the owner re-links their SolaX account (see AddInverter →
    // SolaX → /api/login/solax). Omitted for non-SolaX sites or when never
    // swept, in which case it is treated as N/A.
    solax_token_valid?: boolean;
    solax_token_checked_at?: string;
    // Atmoce access tokens last 30 days and are normally auto-refreshed.
    // false means Atmoce definitively rejected that refresh, so telemetry and
    // control remain stopped until the original linking account re-authorises.
    // Omitted for non-Atmoce sites or an unknown/transient failure.
    atmoce_token_valid?: boolean;
    atmoce_token_checked_at?: string;
    // Amber-fed sites: false = Amber reports the account as closed (owner
    // switched retailer / moved), so no usage data can flow until they
    // reconnect Amber with a current key or switch the site to their active
    // retailer. Omitted for non-Amber sites or when never swept (treated as
    // N/A). Verdict published by amber.first_active_site.
    amber_account_active?: boolean;
    amber_account_hint?: string;
    amber_account_checked_at?: string;
    // Export verification
    export_verified?: boolean | null;
    export_kwh?: number;
    expected_kwh?: number;
    export_records?: number;
    // Export CT cross-check. true = inverter export agrees with the
    // utility meter; false = inverter reported export the meter never
    // recorded (CT/clock/meter contradiction); null = not enough
    // contradicting data in the trailing 2-day window (auto-clears as
    // old intervals age out). This is NOT a warranty or platform fault —
    // it's a site-investigation item owned by the electrician + retailer,
    // and the leading (often remotely-fixable) suspect is a wrong
    // inverter clock. See EXPORT_CT_REMEDIATION / exportCtMismatchTooltip.
    export_ct_match?: boolean | null;
    // How many 5-min intervals over the last 2 days showed the
    // contradiction. Used for context in the tooltip, not as a severity.
    export_ct_mismatches?: number;
    // Worst contradicting intervals (highest claimed export first), each
    // linked to the InverterAction that drove the interval so curious
    // users can self-serve the investigation via
    // /inverter_action_details/{inverter_action_id}.
    export_ct_examples?: Array<{
      time: string;
      grid_power: number;
      meter_export_kwh: number;
      inverter_action_id: number | null;
    }>;
    // Root cause the mismatch pattern fits (only when export_ct_match is false),
    // each with a very different fix:
    //   'export_billed_as_import'   — reversed / wrong-phase CT; the meter books
    //       the export on the IMPORT register, so the customer is charged to export.
    //   'export_not_credited'       — solar-backed export the meter records as
    //       neither export nor import; the meter/NMI may not be crediting exports.
    //   'phantom_export'            — battery-discharge "export" with no meter
    //       export and little solar; a phantom-export CT draining the battery.
    //   'inverter_telemetry_suspect'— claimed export exceeds generation+discharge
    //       (impossible); an inverter-side telemetry/CT/sign fault, not the meter.
    export_ct_diagnosis?:
      | 'export_billed_as_import'
      | 'export_not_credited'
      | 'phantom_export'
      | 'inverter_telemetry_suspect';
    // Server-rendered, actionable explanation of export_ct_diagnosis. Prefer
    // this over local copy so the notice names the specific fix.
    export_ct_hint?: string;
    // Meter import / claimed export over the mismatch intervals — high values
    // are the signature of an export-billed-as-import (reversed CT).
    export_ct_reversed_ratio?: number;
    // Auto-mode self-consumption tracking. In 'auto' the inverter should
    // hold the grid at ~0; sustained drift means it isn't tracking the
    // house load. true = held near zero; false = sustained leak; null =
    // no auto intervals to assess (or not an API-fed inverter). Like
    // export_ct_match this is an amber "needs investigation" signal, NOT a
    // pass/fail readiness gate — surfaced via AutoGridTrackNotice, kept out
    // of checkDescription so it doesn't fold into the red blocker list.
    auto_grid_track?: boolean | null;
    // Flaggable leaking auto intervals (tracking_fault + undersized) and
    // What the manufacturer's own cloud last said about this site's equipment,
    // published only for a site that is ALREADY dark (recent_data === false).
    // true = the vendor lists the hardware as online, so the outage is on the
    // data path between them and Powston and nobody should be sent to the
    // property. false = the vendor lists it as offline, which is a site-side
    // fault the installer owns and can confirm in the vendor's own portal.
    // undefined = we cannot say — most often the plant stopped listing to us
    // at all, which is an access question rather than a fault, so it must never
    // be rendered as either good or bad news. Advisory: surfaced via
    // VendorDeviceStatusNotice and deliberately kept OUT of checkDescription,
    // because recent_data is already the red blocker for a dark site and adding
    // this would double-count the same outage in the readiness score.
    vendor_device_online?: boolean;
    // Companion problem flag (true = fault detected) set only when
    // vendor_device_online is true: reachable equipment, no data, so the gap is
    // Powston-owned. Drives support-queue ownership on the backend; the UI reads
    // vendor_device_online for display. Kept out of checkDescription too — it is
    // failure-polarity, so a true would read there as a passing check.
    vendor_data_path_fault?: boolean;
    // how many auto intervals were examined, for tooltip context.
    auto_grid_leaks?: number;
    auto_grid_auto_intervals?: number;
    auto_grid_tracking_faults?: number;
    auto_grid_undersized?: number;
    // Dominant root cause (only present when auto_grid_track === false):
    //   'tracking_fault' — battery had charge/discharge headroom to hold
    //     zero but the inverter let the grid drift (control / CT / firmware).
    //   'undersized' — battery already at its power limit, so house load (or
    //     surplus solar) exceeded what it could supply/absorb (sizing/load).
    auto_grid_diagnosis?: 'tracking_fault' | 'undersized';
    // Server-rendered actionable explanation of auto_grid_diagnosis. When a
    // leaking interval ran in a dispatch/forced mode the server appends a note
    // that an overriding schedule / inverter config is the likely cause.
    auto_grid_hint?: string;
    // True (only when detected) when a leaking interval's work_mode_label showed
    // a dispatch / forced running mode — e.g. Sungrow "Energy Dispatching
    // Operation" / "Forced Mode Operation" — i.e. an on-inverter schedule or
    // config (commonly a forced-charge window) overrode Powston's auto control.
    // This is a tracking_fault cause, not a CT/firmware fault: the fix is on the
    // inverter's energy-management schedule, not site wiring. Depends on
    // work_mode_label being ingested for the brand.
    auto_grid_dispatch_override?: boolean;
    // Worst leaking intervals (largest |grid_power| first), each linked to
    // the InverterAction so users can self-serve via
    // /inverter_action_details/{inverter_action_id}. 'cause' is the per-row
    // classification ('tracking_fault' | 'undersized' | null).
    auto_grid_examples?: Array<{
      time: string;
      grid_power: number;
      battery_power: number | null;
      house_power: number | null;
      solar_power: number | null;
      battery_soc: number | null;
      // The inverter's reported running mode during the interval (e.g. Sungrow
      // "Self-consumption Operation", "Energy Dispatching Operation", "Forced
      // Mode Operation"). A dispatch/forced label is what drives
      // auto_grid_dispatch_override. Null when the brand doesn't report it.
      work_mode_label?: string | null;
      cause: 'tracking_fault' | 'undersized' | null;
      inverter_action_id: number | null;
    }>;
    // --- Peak trading-readiness signals (battery vs the evening peak) --------
    // Allowed values (backend contract): the main flags are `true` when the
    // pattern is raised, otherwise `null` (Python None) — never `false`. The key
    // is always present once get_site_checks has run, but the `?` covers older
    // cached payloads from before this shipped, where it may be absent. These are
    // advisory, severity-graded, and kept out of the red blocker list (not in
    // checkDescription) and the readiness percentage — the UI gates on `=== true`.
    //
    // battery_capacity_insufficient_for_peak: the battery empties BEFORE the
    // evening peak ends, forcing expensive late-peak imports — a sizing /
    // architecture shortfall. true when the recurring pattern is raised.
    battery_capacity_insufficient_for_peak?: true | null;
    battery_peak_severity?: 'warning' | 'major' | 'critical';
    battery_peak_hint?: string;
    battery_peak_valid_nights?: number;
    battery_peak_failing_nights?: number;
    battery_peak_expensive_import_kwh?: number;   // avg per failing night
    battery_peak_expensive_import_cost?: number;  // avg $ per failing night
    battery_peak_duration_hours?: number | null;  // capacity / discharge rating
    battery_peak_duration_short?: true;           // true (only when < 1.5h), else absent
    battery_peak_usable_kwh?: number;
    battery_peak_evening_load_exceeds_nights?: number;
    battery_peak_evening_load_exceeds_battery?: true;  // true (only when exceeded), else absent
    battery_peak_examples?: Array<{
      date: string;
      depletion_time: string;
      peak_remaining_min: number;
      expensive_import_kwh: number;
      expensive_import_cost: number;
      weighted_avg_buy_price: number;
      inverter_action_id: number | null;
    }>;
    // battery_not_ready_for_peak: the battery enters the peak below full SOC and
    // then buys expensive peak energy, when cheaper energy earlier in the day
    // could have charged it — a pre-peak charging / trading-readiness problem.
    battery_not_ready_for_peak?: true | null;
    battery_ready_severity?: 'warning' | 'major' | 'critical';
    battery_ready_hint?: string;
    battery_ready_valid_days?: number;
    battery_ready_failing_days?: number;
    battery_ready_examples?: Array<{
      date: string;
      peak_start_soc: number;
      target_peak_soc: number;
      battery_headroom_kwh: number;
      peak_import_kwh: number;
      weighted_avg_peak_price: number;
      cheapest_pre_peak_price: number;
      missed_cheap_import_kwh: number;
      estimated_extra_cost: number;
      attempted_import_before_peak: boolean;
      inverter_action_id: number | null;
    }>;
    // import_command_not_followed_before_peak: Powston issued a pre-peak charge
    // (import) command but the battery did not charge — a control / commissioning
    // issue (inverter, battery, comms or config), not a trading-strategy one.
    import_command_not_followed_before_peak?: true | null;
    import_command_hint?: string;
    import_command_valid_days?: number;
    import_command_failing_days?: number;
    import_command_examples?: Array<{
      command_start: string;
      command_end: string;
      requested_action: string;
      soc_start: number;
      soc_end: number;
      expected_soc_gain: number;
      actual_soc_gain: number;
      grid_import_kwh: number;
      grid_export_kwh: number;
      meter_net_kwh: number;
      intervals: number;
      inverter_action_id: number | null;
    }>;
    // --- Generation-visibility + tariff-window warnings (amber, advisory) ----
    // Same contract as the peak signals above: each flag is `true` when raised,
    // else `null` (never `false`); always present once get_site_checks has run
    // (`?` only covers older cached payloads). Out of checkDescription / readiness;
    // the UI gates on `=== true`.
    //
    // uncontrolled_ac_coupled_generation_detected: the utility meter exports
    // more than the controlled inverter's own CT sees (or house_power <= 0 while
    // exporting) — generation Powston can't see/control (AC-coupled / legacy).
    uncontrolled_ac_coupled_generation_detected?: true | null;
    uncontrolled_ac_intervals?: number;
    uncontrolled_ac_hint?: string;
    uncontrolled_ac_examples?: Array<{
      time: string;
      house_power: number | null;
      meter_export_kwh: number;
      inverter_export_kwh: number;
      excess_kwh: number;
      inverter_action_id: number | null;
    }>;
    // uncommanded_grid_import_detected: the utility meter shows battery-scale
    // grid import in intervals where Powston did NOT command a buy (it was
    // selling / discharging / on self-use) while the battery still has charge —
    // i.e. the inverter is grid-charging on its own, against the command. The
    // fingerprint of an uncoordinated multi-inverter / CT / parallel-commissioning
    // fault (installer-owned, not trading logic).
    uncommanded_grid_import_detected?: true | null;
    uncommanded_grid_import_intervals?: number;
    uncommanded_grid_import_hint?: string;
    uncommanded_grid_import_examples?: Array<{
      time: string;
      commanded_action: string;
      soc: number | null;
      buy_price: number | null;
      meter_import_kwh: number;
      meter_export_kwh: number;
      inverter_action_id: number | null;
    }>;
    // foreign_modbus_writer: a SECOND Modbus master is writing the same registers
    // Powston writes, so our commands are overwritten and the site oscillates
    // between the two. Owner-owned — commonly a Home Assistant Sungrow Modbus
    // integration, an iSolarCloud schedule, or another EMS — and deliberately kept
    // OUT of checkDescription: it is not a Powston or installer blocker, and the
    // owner is the only party who can disconnect the other controller.
    //
    // The verdict needs a foreign write observed in the last 24h, so it clears
    // within a day of that controller being disconnected. `_last_seen` is also
    // returned alongside a null verdict when contention stopped inside the
    // telemetry window, which is how support tells "it stopped" from "never
    // happened" — so do NOT render on `_last_seen` alone.
    foreign_modbus_writer?: true | null;
    // 'warning' when a clear coincided with a battery-scale export breach,
    // otherwise 'info' (the support queue's PRODUCER_SEVERITY vocabulary).
    foreign_modbus_writer_severity?: 'warning' | 'info';
    foreign_modbus_writer_last_seen?: string;
    foreign_modbus_writer_enable_clears?: number | null;
    foreign_modbus_writer_export_breaches?: number | null;
    foreign_modbus_writer_hint?: string;
    // The standing position on third-party Modbus tools, rendered verbatim.
    foreign_modbus_writer_notice?: string;
    foreign_modbus_writer_setpoints?: Array<{
      value: number;
      samples: number;
      runs: number;
      first_seen: string;
      last_seen: string;
      last_written: string;
    }>;
    // export_limit_may_not_cover_all_generation: an export limit is set on the
    // controlled inverter but total utility export still exceeds it (trading +
    // DNSP-compliance risk).
    export_limit_may_not_cover_all_generation?: true | null;
    export_limit_over_intervals?: number;
    export_limit_kwh_per_interval?: number;
    export_limit_hint?: string;
    export_limit_examples?: Array<{
      time: string;
      meter_export_kwh: number;
      limit_kwh: number;
      over_by_kwh: number;
      inverter_action_id: number | null;
    }>;
    // tariff_window_mismatch: the empirical daily price peak is a stable window
    // sitting outside Powston's assumed 16:00-21:00 peak, so reserve/charging
    // decisions fire at the wrong time.
    tariff_window_mismatch?: true | null;
    tariff_window_peak_days?: number;
    tariff_window_hint?: string;
    tariff_window_observed_peak_hour?: number;
    tariff_window_assumed?: string;
    tariff_window_observed?: string;
    // controlled_load_circuit_detected: Local Volts NMI metadata still carries a
    // controlled-load ('CLoad') circuit — network-switched with no spot-price
    // awareness, so cheaply-exported solar can be re-imported at peak prices to
    // run the controlled load. Local Volts sites only; the remedy is asking for
    // its removal via the "Talk to Local Volts" form.
    controlled_load_circuit_detected?: true | null;
    controlled_load_hint?: string;
    controlled_load_tariffs?: string[];
    // --- Battery power-capability signals (charge/discharge below the inverter
    // rating, and thermal derating). Advisory, severity-graded, `true | null` only,
    // kept OUT of checkDescription (not red blockers). The UI gates on `=== true`.
    //
    // export_below_inverter_capability: battery discharge plateaus below the inverter
    // rating during commanded exports — a config cap (DEYE current / SAJ cluster /
    // Sungrow modules) or a battery rated below the inverter, losing export power.
    export_below_inverter_capability?: true | null;
    discharge_throttle_intervals?: number;
    discharge_peak_w?: number;
    discharge_rated_w?: number;
    discharge_capability_frac?: number;
    discharge_throttle_severity?: 'warning' | 'major' | 'critical';
    discharge_throttle_hint?: string;
    // Names the limiting DEYE setting when cached, e.g. 'DEYE maxDischargeCurrent=50A'.
    discharge_throttle_setting?: string;
    discharge_throttle_example?: { time: string; battery_power: number; inverter_action_id: number | null };
    // import_below_inverter_capability: the same, for charging — the battery can't
    // soak cheap / negative-priced grid energy at the inverter's rating.
    import_below_inverter_capability?: true | null;
    charge_throttle_intervals?: number;
    charge_peak_w?: number;
    charge_rated_w?: number;
    charge_capability_frac?: number;
    charge_throttle_severity?: 'warning' | 'major' | 'critical';
    charge_throttle_hint?: string;
    charge_throttle_setting?: string;
    charge_throttle_example?: { time: string; battery_power: number; inverter_action_id: number | null };
    // battery_thermal_derating: battery power suppressed at temperature extremes
    // (hot/cold) vs the site's own normal-temperature peak. Gated on battery_temperature
    // ingest (Sungrow/SAJ/FoxESS/Sonnen today; not Sigenergy/DEYE yet).
    battery_thermal_derating?: true | null;
    thermal_derating_extreme?: 'hot' | 'cold' | 'both';
    thermal_derating_intervals?: number;
    thermal_normal_peak_w?: number;
    thermal_temp_min_c?: number | null;
    thermal_temp_max_c?: number | null;
    thermal_derating_hint?: string;
    // alphaess_unbound: one or more AlphaESS inverters on the site aren't bound to
    // Powston's service account, so the cloud API returns "No Permission" on every
    // pull and reports no SOC/battery/solar data. Names the offending serial(s).
    // Advisory `true | null`; the UI gates on `=== true`.
    alphaess_unbound?: true | null;
    alphaess_unbound_serials?: string[];
    alphaess_unbound_hint?: string;
    // inverter_ip_changed: a LOCKED controller whose last LAN scan found the inverter
    // at a different IP than it's configured to poll (a DHCP lease change). Powston
    // won't self-correct a locked IP, so it stays pinned to the old address until the
    // owner updates it. Surfaced by InverterIpChangedNotice; gates on `=== true`.
    inverter_ip_changed?: boolean;
    inverter_ip_changed_detail?: {
      severity: 'medium' | 'high';
      configured_ip: string | null;
      // discovered_ip is set ONLY when the scan is unambiguous (one responder); when
      // several responders are found it's null and discovered_ips lists the candidates.
      discovered_ip: string | null;
      discovered_ips?: string[];
      ambiguous?: boolean;
      receiving_recent_data: boolean;
      data_age_minutes: number | null;
      message: string;
    };
    // inverter_ip_moved: an UNLOCKED controller whose IP just moved (DHCP lease change)
    // that Powston auto-healed for the owner — connection already restored, but there was
    // a data gap so some trades may have been missed. Advisory only (Powston fixed the IP);
    // it nudges a DHCP reservation so it stops recurring. Surfaced by InverterIpMovedNotice;
    // gates on `=== true`. Distinct from inverter_ip_changed, which is the LOCKED case the
    // owner must fix themselves.
    inverter_ip_moved?: boolean;
    inverter_ip_moved_detail?: {
      severity: 'warning';
      previous_ip: string | null;
      new_ip: string | null;
      moved_at: string | null;
      last_data_at: string | null;
      gap_minutes: number | null;
      message: string;
    };
    // Training runs
    training_runs?: Array<{
      id: number;
      meter_bill: number;
      bill: number;
      start_date: string;
      end_date: string;
      started_at: string;
      finished_at: string;
      created_at: string;
    }>;
  };

  // Phase 0 of doc/guidelines.md: three-role dashboards.
  // Customer is the default — every authenticated user not flagged as
  // installer or admin sees the customer view.
  export type Role = 'customer' | 'installer' | 'admin';

  // Guideline §3: every user-visible problem must name an owner. The
  // owner drives both copy ("contact your installer") and gating
  // ("escalation requires installer-owned checks resolved first").
  export type ProblemOwner =
    | 'customer'
    | 'installer'
    | 'retailer'
    | 'oem'
    | 'powston'
    | 'observation';

  // Guideline §4: site-level readiness state, computed from
  // OnboardingChecks. The whole UI gates off this status — customers
  // see one sentence, installers see the failed checks behind it.
  export type TradingReadyStatus =
    | 'ready'
    | 'partial'
    | 'not_ready'
    | 'monitoring_only'
    | 'control_blocked'
    | 'unknown';

  export type TradingReadyCheck = {
    key: string;
    label: string;
    passed: boolean;
    owner: ProblemOwner;
    // Diagnosed cause for the failure when we can tell one from the data
    // (e.g. nmi_recent failing because the DNSP says a different retailer
    // is responsible for the NMI). Absent when the failure is generic.
    reason?: string;
    // Per-role next-action copy. Omit a role to hide the check from it.
    nextAction: {
      customer?: string;
      installer?: string;
    };
  };

  export type TradingReadyResult = {
    status: TradingReadyStatus;
    summary: string;
    checks: TradingReadyCheck[];
    failed: TradingReadyCheck[];
    primaryOwner: ProblemOwner | null;
  };

  export type Take5Answer = 'yes' | 'no' | 'na' | null;

  export interface Take5Assessment {
    understandsTask: Take5Answer;
    trainedForWork: Take5Answer;
    correctPPE: Take5Answer;
    informedOthers: Take5Answer;
    hasApprovalDocs: Take5Answer;
    correctTools: Take5Answer;
    hazardAssessmentComplete: Take5Answer;
    hazardsMadeSafe: Take5Answer;
  }

  export interface OnReportSite {
    inverter_id: number;
    site_id: number;
    serial_number: string;
    action: string;
    reason: string;
    costs: number;
    user_code_name: string;
    battery_soc: number;
    site_name: string;
    owner_name: string;
    data_logger_controlled: boolean;
    powston_managed: boolean;
    APP_VERSION: string;
    BATTERY_SOC_NEEDED: number;
    BATTERY_FOR_AC: number;
    BAD_SUN_DAY_KEEP_SOC: number;
    GOOD_SUN_DAY: number;
    GOOD_SUN_HOUR: number;
    IMPORT_TOLERANCE: number;
    BATTERY_SOC_BUYBACK: number;
    BUY_DELTA_THRESHOLD: number;
    CUT_OFF_THRESHOLD: number;
    MORNING_SELL_MARGIN: number;
    MORNING_SELL_SOC: number;
    BATTERY_EXPORT_SOC_THRESHOLD: number;
    ALWAYS_IMPORT_SOC: number;
    DEFAULT_EXPORT: number;
    BATTERY_SOC_AC: number;
    P2P_EXPORT_POWER: number;
    P2P_EXPORT_START_HOUR: number;
    P2P_EXPORT_END_HOUR: number;
  }

  export const checkDescription: Record<string, string> = {
    nmi: "NMI present",
    nmi_reporting: "Meter data: within 10 days",
    nmi_actual: "Getting actual utility meter data",
    nmi_recent: "Meter data: within 36 hours",
    dnsp_address_match: "DNSP address matches site",
    dnsp_eg_registered: "Solar registered with the network (DNSP)",
    has_site_monitor: "Has site monitor",
    site_monitor_configured: "Site monitor configured",
    tariffs: "Best Tariffs selected",
    site_monitor_online: "Site monitor online",
    site_monitor_inverter: "Site monitor inverter",
    has_inverter: "Has inverter",
    site_monitor_local: "Local data reporting",
    inverter_controlled: "Inverter controlled",
    user_code: "Magic Mode code present",
    recent_data: "Recent data from device",
    recent_modbus_data: "Recent Modbus data available",
    user_code_enabled: "Magic Mode code enabled",
    site_location: "Site location set",
    site_timezone: "Site timezone set",
    owner_set: "Site has an owner",
    battery_capacity_set: "Battery capacity configured",
    network_valid: "Network valid for state",
    recent_action: "Recent rule action recorded",
    export_verified: "Export verified",
    saj_aio3_powston_firmware: "SAJ AIO3 Powston firmware installed",
    saj_local_active: "SAJ local control active",
    solax_token_valid: "SolaX account connected",
    atmoce_token_valid: "Atmoce account connected",
    amber_account_active: "Amber account active",
    // NOTE: export_ct_match is intentionally absent. It is an amber
    // "needs investigation" item, not a pass/fail trading-readiness
    // check — adding it here would fold it into the red failure list and
    // the system-health count. It is surfaced separately via
    // ExportCtMismatchNotice / exportCtMismatchTooltip.
  };

  // Remediation copy for an export CT mismatch (inverter reports export
  // to grid, utility meter recorded none). Cheapest-first ordering: the
  // leading suspect is a drifted inverter clock that mislabels intervals
  // (often fixable remotely), then CT wiring/phase, then the meter.
  // Mirrors the server-side EXPORT_CT_REMEDIATION constant.
  export const EXPORT_CT_REMEDIATION =
    "Not a warranty fault, but in the local electrician's / retailer's domain: " +
    "first verify the inverter clock/time is correct (a wrong clock misaligns " +
    "intervals and can fake this), then check the CT wiring / phase and, while " +
    "on site, the utility meter.";

  // Short tooltip variant for badges, parameterised with the interval count.
  export const exportCtMismatchTooltip = (mismatches?: number): string =>
    `Inverter is reporting export to grid but the utility meter recorded none ` +
    `(${mismatches ?? 0} intervals, last 2 days). Not a warranty issue — for the ` +
    `electrician/retailer: check the inverter clock first (a wrong time can cause ` +
    `this), then CT wiring/phase and the utility meter.`;

  export type Check = {
    name: string,
    site_id: number,
    inverter_id: number,
    serial_number: string,
    action: string,
    time: string,
    updated_at: string,
    timezone: string,
    minutes_ago: number,
    state: string,
    tariff: string,
    day_cost: number,
    day_paid: number,
    day_paid_rate: number,
    day_made: number,
    day_made_rate: number,
    week_cost: number,
    week_paid: number,
    week_made: number,
    retail_cost: number,
    retail_rebate: number,
    day_general_kwh: number,
    day_off_peak_kwh: number,
    day_peak_kwh: number,
    day_battery_savings: number,
    day_ppv_consumed_cost: number,
    week_battery_savings: number,
    week_ppv_consumed_cost: number,
    peak_cost: number,
    off_peak_cost: number,
    day_feed_in_kwh: number,
    day_general_tariff: number,
    day_feed_in_tariff: number,
    pgrid: number,
    watch_pgrid: boolean,
    pbattery1: number,
    ppv: number,
    three_day_cost: number,
    battery_soc: number,
    retail_yesterday_cost: number,
    retail_yesterday_earnings: number;
    retail_yesterday_buy: number,
    retail_yesterday_buy_rate: number,
    retail_yesterday_sell_rate: number,
    week_retail_cost: number,
    retail_yesterday_cost_png: string,
    estimated_yesterday_bill: number,
    expensive_import_cost: number,
    expensive_import_kwh: number,
    expensive_export_rebate: number,
    expensive_export_kwh: number,
    retail_vs_estimated: number,
    house_consumption: number,
    ev_meter_values: EvMeterData,
    power_meters: PowerMeter[],
  }

export type MDNSEntry = {
  'IP': string;
  'Hostname': string;
  'Port': number;
  'Txt0'? : string;
  'Txt1'? : string;
  'Txt2'? : string;
  'Txt3'? : string;
}

export type MDNSEntryDict = {
  [key: string]: MDNSEntry;
}

export type ScanDataEntryDict = {
  [key: string]: string
}

export type ModbusConfigDict = {
  [key: string]: any;
}

export type SiteMonitor = {
  'id': number;
  'firmware_version': string;
  'pending_firmware_version': string;
  'monitor_type': string;
  'site_id': number | null;
  'inverter_id': number;
  'ip_address': string;
  'mqtt_topic': string;
  'last_boot_at': Date;
  'mac_address': string;
  'serial_number': string;
  'mqtt_user': string;
  'mqtt_pass': string;
  'manufacturer': string;
  'product_name': string;
  'modbus_slave_id': number;
  'modbus_type': string;
  'modbus_tcp_ip': string;
  'modbus_tcp_port': number;
  'modbus_timeout'? : number;
  'loop_interval'?: number;
  'retain_settings'?: boolean;
  'mdns_data'?: MDNSEntryDict;
  'scan_data'?: ScanDataEntryDict;
  'config'?: ModbusConfigDict;
  'auth_token'?: string;
  'network_scan'?: string | null;
  'ip_locked'?: boolean;
  // Switch-port watch over read-only SNMP v2c (esp32-monitor #84, firmware
  // 26.10.05). The community itself is write-only: GET returns only whether
  // one is set, and `snmp_community` is sent on PUT only when the user typed
  // a new value ("off" disables the watch).
  'snmp_host'?: string;
  'snmp_if'?: string;
  'snmp_community_set'?: boolean;
  'snmp_community'?: string;
}

/** Controller switch-watch status (`sw_status` / `switch.status`). */
export type SwitchStatus =
  | 'ok'
  | 'all_ports'
  | 'port_unknown'
  | 'timeout'
  | 'no_agent'
  | 'mac_unknown'
  | 'not_configured'
  | 'off';

/** One port row of an all-ports watch. */
export type SwitchPort = {
  i: number;           // ifIndex
  n?: string;          // ifName / ifDescr
  o?: string;          // oper status: up, down, dormant, ...
  changed_s?: number;  // seconds since the last oper change
  mbps?: number;
};

/** Compact event as carried inside the switch object's RAM ring. */
export type SwitchRingEvent = {
  ts?: number;
  up?: number;
  i?: number;
  oper?: string;
  prev_oper?: string;
  changed_s?: number;
  mac_seen?: boolean;
  flap?: boolean;
  err_delta?: number;
};

/** Discovery verdict for one SNMP agent the controller tried. */
export type SwitchAgentVerdict = {
  ip: string;
  r: string;
};

export type SwitchDiscovery = {
  age_s?: number;
  sweep?: number;
  agents?: SwitchAgentVerdict[];
};

/**
 * Switch object as reported by the controller (`modbus-monitor/{mac}/switch`
 * reply, or `scan_result.target_probe.switch`). The all-ports variant carries
 * `ports`; the single-port variant carries the watched port at top level.
 */
export type SwitchState = {
  status: SwitchStatus | string;
  mac?: string;
  agent?: string;
  ports?: SwitchPort[];
  age_s?: number;
  events?: SwitchRingEvent[];
  disc?: SwitchDiscovery;
  // Single-port variant
  if?: string;
  if_index?: number;
  oper?: string;
  changed_s?: number;
  speed_mbps?: number;
  in_err?: number;
  out_err?: number;
  mac_seen?: boolean;
  pinned?: boolean;
  bridge_port?: number;
  macs?: number;
  [key: string]: unknown;
};

/** Edge published on `modbus-monitor/{mac}/port_event`, as stored by the backend. */
export type SwitchPortEvent = {
  ts?: number;
  up?: number;
  oper?: string;
  prev_oper?: string;
  changed_s?: number;
  speed_mbps?: number;
  err_delta?: number;
  mac_seen?: boolean;
  flap?: boolean;
  agent?: string;
  if?: string;
  if_index?: number;
  all_ports?: boolean;
  pinned?: boolean;
  received_at?: string;
};

export type SwitchInterface = {
  i: number;
  n?: string;
  o?: string;
  mbps?: number;
  chg?: number;
};

/** Reply to the `list` command. */
export type SwitchInterfaces = {
  status: 'ok' | 'incomplete' | 'no_answer' | 'no_agent' | 'bad_agent' | 'not_configured' | string;
  agent?: string;
  ifs?: SwitchInterface[];
  more?: boolean;
  no_chg?: boolean;
};

/** `GET /controller_switch/{mac}` */
export type ControllerSwitchResponse = {
  mac: string;
  settings: {
    snmp_community_set: boolean;
    snmp_host: string;
    snmp_if: string;
  };
  heartbeat: {
    sw_status: string | null;
    sw_agent: string | null;
    sw_if: string | null;
    target_mac: string | null;
    at: string | null;
  };
  switch: SwitchState | null;
  switch_at: string | null;
  switch_source: 'reply' | 'scan_result' | null;
  events: SwitchPortEvent[];
  interfaces: SwitchInterfaces | null;
  interfaces_at: string | null;
};

export type SwitchCommand = 'probe' | 'discover' | 'list';
export type UtilityMeter = {
  'id': number;
  'site_id': number;
  'nmi': string;
  'state': string;
  'region': string;
  'tariff': string;
  'network': string;
  'timezone': string;
  'lnsp': string;
  'tni': string;
  'dlf': number;
  'import_tariff': string;
  'export_tariff': string;
};
export type UtilityMeterData = {
  'id': number;
  'interval_time': Date;
  'energy_export': number;
  'energy_import': number;
  'rrp': number;
  'earnings': number;
  'costs': number;
  'energy_costs': number;
  'fixed_costs': number;
  'feed_in_kwh': number;
  'general_kwh': number;
  'house_consumption': number;
  'ppv_generation': number;
  'start_battery_soc': number;
  'quality': string;
  'battery': number;
  'duration': number;
}

export type MeterInterval = '5min' | 'hour' | 'day';

// Returned by GET /api/power_meter_data/{power_meter_id}?start_date=...&end_date=...&interval=5min
// Per-device 5-min bucketed reading. Sorted ascending by time. nulls in
// energy_kWh / cents_per_kwh are filled to 0 server-side. `cost` is a
// legacy alias of `estimated_cost` — use estimated_cost.
export type PowerMeterReading = {
  'time': string;
  'site_name': string;
  // PowerMeter.fn — may be empty; fall back to the daily summary's `name`.
  'device_name': string;
  'max_power': number;
  // Running odometer at end of bucket (kWh) — for sanity checks / reset detection only.
  'total_energy': number;
  // kWh consumed during this bucket (LAG diff of total_energy).
  'energy_kWh': number;
  // Cents spent in this bucket. Divide by 100 for dollars.
  'estimated_cost': number;
  'cost': number;
  // AVG buy price during the bucket, c/kWh.
  'cents_per_kwh': number;
  // Wholesale spot price, $/MWh — divide by 10 for c/kWh to overlay with cents_per_kwh.
  'rrp': number;
}

// Returned by GET /api/meter_data/{site_id}/{power_meter_id}?interval=5min
// Five-minute bucket — same fields the legacy endpoint produced, plus
// forecasts and the action that was billed against the meter for the interval.
//
// IMPORTANT: the backend (json_sanitize) OMITS any key whose value is
// null/NaN rather than sending null, so every field except `interval_time` is
// optional and is never `null` — treat a missing key as "no data for this
// column in this interval". Floats are rounded to 5 decimals server-side.
export type MeterDataInterval = {
  // ISO-8601 with the site's local UTC offset, e.g. "2026-05-30T12:45:00+10:00".
  // The END of the interval: label "12:45" covers [12:40, 12:45).
  'interval_time': string;
  'energy_import'?: number;        // kWh
  'energy_export'?: number;        // kWh
  'battery_w'?: number;            // watts: + discharging, - charging
  'start_battery_soc'?: number;    // % at interval start
  'rrp'?: number;                  // actual wholesale spot, $/MWh
  'hhf_rrp'?: number;              // half-hour forecast spot, $/MWh
  'forecasts'?: number[];          // forward RRP forecast curve, $/MWh
  'solar_power'?: number;          // PV generation, watts (>= 0)
  'house_power'?: number;          // house consumption, watts (>= 0)
  'billed_costs'?: number;         // cents; includes billed_fixed_costs
  'billed_fixed_costs'?: number;   // cents; daily supply charge spread over the interval
  'billed_earnings'?: number;      // cents
  'billed_action'?: string;        // decision label, e.g. "charge" / "discharge"
  'buy_price'?: number;            // import tariff, c/kWh
  'sell_price'?: number;           // export/feed-in tariff, c/kWh
}

// Returned at interval=hour or interval=day. Aggregated: energies and cent-denominated billing values
// are summed; power, price and SOC are averaged. Forecasts and the
// billed_action column are dropped at this resolution. Same null-omission
// rule as MeterDataInterval: every field except interval_time is optional.
export type MeterDataAggregate = {
  'interval_time': string;
  'energy_import'?: number;
  'energy_export'?: number;
  'battery_w'?: number;
  'start_battery_soc'?: number;
  'rrp'?: number;
  'hhf_rrp'?: number;
  'solar_power'?: number;
  'house_power'?: number;
  'billed_costs'?: number;         // cents; includes billed_fixed_costs
  'billed_fixed_costs'?: number;   // cents; informational cost component
  'billed_earnings'?: number;      // cents
  'buy_price'?: number;            // import tariff, c/kWh
  'sell_price'?: number;           // export/feed-in tariff, c/kWh
}

// ─── DNSP address verification types ────────────────────────────────────────
// Returned by GET /api/dnsp_address_check/{site_id}?max_distance_m=N (admin-only).
// Compares the site's address against DNSP/utility-meter premise addresses
// to flag when settled meter data may be attributed to the wrong location.
// Unlike MeterDataInterval, this endpoint DOES send explicit nulls.
export type DnspCandidate = {
  utility_meter_id: number;
  nmi: string | null;
  address: string | null;
  address_summary: string | null;
  postcode: string | null;
  lat: number | null;
  lng: number | null;
  /** haversine distance in metres; null if either point failed to geocode */
  distance_m: number | null;
  postcode_match: boolean;
  is_close: boolean;
  within_distance: boolean;
  /** Which site currently holds this NMI. Present on better_matches entries
   *  (the admin fix UI uses it to warn before stealing a meter from another
   *  site); may be absent on the assigned `matched` candidate. */
  site_id?: number | null;
};

export type DnspMeterCheck = {
  utility_meter_id: number;
  assigned_nmi: string | null;
  /** evaluation of the currently-assigned NMI's DNSP premise record */
  matched: DnspCandidate | null;
  postcode_mismatch: boolean;
  is_close: boolean;
  flagged: boolean;
  /** other NMIs whose DNSP premise address fits the site address better */
  better_matches: DnspCandidate[];
};

export type DnspAddressCheck = {
  site_id: number;
  site: {
    location: string | null;
    postcode: string | null;
    state: string | null;
    lat: number | null;
    lng: number | null;
    geocoded: boolean;
  };
  max_distance_m: number;
  /** true if any meter in the list is flagged */
  flagged: boolean;
  meters: DnspMeterCheck[];
};

// Returned by POST /api/dnsp_address_check/{site_id}/apply?dry_run=true|false
// (admin). The backend self-selects the single unambiguous within-threshold,
// postcode-matching candidate, re-links that meter and sets it as the site's
// primary. `eligible` is the backend's own go/no-go; `changes` describes what
// was (or would be) altered. `dry_run` defaults to true server-side — only
// dry_run=false mutates. `result` carries the underlying evaluation detail.
export type DnspApplyResult = {
  site_id: number;
  eligible: boolean;
  dry_run: boolean;
  applied: boolean;
  // Shape is backend-defined; render defensively (string or object per item).
  changes: Array<Record<string, unknown> | string>;
  result?: unknown;
};

export type UsageData = {
  'time': Date;
  'pgrid': number;
  'house_consumption': number;
  'ppv': number;
  'battery_soc': number;
  'pbattery1': number;
  'Power to grid': number;
  'Power from grid': number;
}

export type ManualAction = {
  'id': number;
  'inverter_id': number;
  'action': string;
  'reason':  string;
  'notes': string;
  'min_sell_price': number | null;
  'max_sell_price': number | null;
  'min_buy_price': number | null;
  'max_buy_price': number | null;
  'min_soc': number | null;
  'max_soc': number | null;
  'time': Date;
  'start_time': Date;
  'end_time': Date;
  'action_params': Record<string, any>;
  // Outcome/audit fields are optional while older API deployments are still
  // returning the original ManualAction shape.
  'created_at'?: Date;
  'requested_at'?: Date;
  'effective_at'?: Date;
  'applied_at'?: Date;
  'actor'?: string | Record<string, unknown>;
  'requested_by'?: string | Record<string, unknown>;
  'user_code'?: string;
  'user'?: string | Record<string, unknown>;
  'status'?: string;
  'state'?: string;
  'result'?: unknown;
  'result_message'?: string;
  'outcome'?: string;
  'failure_reason'?: string;
  'failure_note'?: string;
  'error_message'?: string;
  'error'?: string;
}

export type InverterAction = {
  forecast_sell_price: number
  'id': number;
  'inverter_id': number;
  'action': string;
  'reason': string;
  'values': string;
  'time': Date;
  'interval_time': Date;
  'start_soc': number;
  'end_soc': number;
  'pbattery': number;
  'e_grid': number;
  'energy_import': number;
  'energy_export': number;
  'earnings': number;
  'costs': number;                 // cents; settled rows bundle energy_costs + fixed_costs
  'energy_costs'?: number;         // cents; cost of the energy imported this interval (settled rows)
  'fixed_costs'?: number;          // cents; daily supply charge spread over the interval (settled rows)
  'quality': string;
  'start_time': Date;
  'end_time': Date;
  'date'? : string;
  'buy_price'?: number;
  'sell_price'?: number;
  'sell_forecast'?: number[];
  'buy_forecast'?: number[];
  'modbus_settings'?: ControlMessage | null;
  'utility_meter_record_id'?: number | null;
}

export type ApiAction = {
  id: number;
  inverter_id: number;
  action: string;
  status_code?: number | null;
  url?: string | null;
  parameters?: Record<string, any> | null;
  body?: Record<string, any> | null;
  created_at: Date;
}

export type ApiResponse = {
  id: number;
  api_action_id?: number | null;
  response?: Record<string, any> | null;
  created_at?: Date;
  updated_at?: Date;
};

export type DailyCost = {
  'start_date': Date;
  'end_date': Date;
  'tariff': string;
  'daily_cost': number;
}

export type InverterData = {
  'site': Site;
  'inverter': Inverter;
  'runtime_data': RuntimeData;
}

export type PowerMeter = {
  'id': number;
  'site_id': number;
  'site_name'?: string;
  'dn'?: string;
  'today': number;
  'yesterday': number;
  'power': number;
  'time': Date;
  'state': string;
  // `name` is guaranteed non-empty by the backend (fn → dn → "Meter <id>").
  'name': string,
  // Cents spent on this meter since midnight (Brisbane). SUM of
  // estimated_cost across the day window. Divide by 100 for dollars.
  'day_cost': number,
  // Cheap-window lookback in hours for price-based scheduling. threshold=4
  // means "run during the cheapest 4 hours of the day". null disables
  // price-based switching. NOT a watts threshold.
  'threshold': number | null,
  // Cents/kWh ceiling for letting the meter run on grid power. null = no limit;
  // 0 means "only when energy is free" — null and 0 are NOT interchangeable.
  'max_buy_price': number | null,
  // Parent-device label that ties channels of the same physical meter
  // together. A PC341 reports its mains DP plus N CT channels, all sharing
  // the same `location` string. Empty string when unset.
  'location': string,
  // Wire protocol used to read this meter. Drives UI behaviour: hide
  // threshold / max_buy_price for CT channels because those settings only
  // make sense on a switchable load. Known values: "tuya_ct", "tasmota",
  // "ha", "emporia". Empty string when unset.
  'protocol': string,
  // False when the meter has never reported. UI must show "No data" instead
  // of trusting power/state/time, because time falls back to NOW() in this
  // case to keep the field non-nullable.
  'has_recent_reading': boolean,
  // Settlement state of the most recent reading's price. Promotes Est → Exp → Act
  // as retailer data lands. null when no pricing has been applied yet (rare, ≤15 min).
  'pricing_quality': 'Est' | 'Exp' | 'Act' | null,
  // When true the user-facing UI must omit this meter from all lists and
  // summaries. Set by backend admin tooling — there is no in-app toggle.
  'hidden': boolean,
}


export interface PacketInfo {
  registerTypeAndAddress: string;
  timestamp: number;
}

export interface InverterRegisters {
    id: number
    inverter_id: number;
    registers: ModbusConfig;
    created_at?: Date;
    updated_at?: Date;
}

export interface ModbusConfigItem {
  address: number;
  name: string;
  unit: string;
  valueMap?: string;
  category: string;
  scale?: number;
  signed?: boolean;
  size?: number;
  offset?: number;
  required?: boolean;
  description?: string;
  encoding?: BufferEncoding;
  dataKey: string;
  length?: number;
  byte_order?: 'ABCD' | 'CDAB' | 'BADC' | 'DCBA';
  slaveId?: number;
  downscale_over?: number;
  registerType: 'holding' | 'input';
  dataType: 'U16' | 'S16' | 'U32' | 'S32' | 'U64' | 'S64' | 'F32' | 'FLOAT32' | 'FLOAT16' | 'ENUM' | 'STR' | 'STR16' | 'int+SF' | 'sunsf';
}

export interface ModbusConfig {
  [category: string]: {
    [key: string]: ModbusConfigItem;
  };
}

export const BasicModes = ['auto',
                       'charge',
                       'discharge',
                       'import',
                       'export',
                       'stopped',
                      ] as const;

export const DefaultModes = ['auto', 'auto-curtail',
                       'charge', 'charge-curtail',
                       'discharge', 'discharge-curtail',
                       'import', 'import-curtail',
                       'export', 'export-curtail',
                       'stopped', 'stopped-curtail'
                      ] as const;

export interface VariableEntry {
  name: string;
  value: string;
  comment: string;
  lineIndex: number;
  prefix: string;
  postfix: string;
}

export function extractConstantsFromCode(code: string) {
    const lines = code.split(/\r?\n/);
    const vars: VariableEntry[] = [];

    lines.forEach((line, idx) => {
        // Regex to match ALL_CAPS constants with optional comment
        const match = line.match(/^(\s*([A-Z0-9_]+)\s*=\s*)([^#]*)(#?.*)$/);
        // Explanation: 
        // match[1] = prefix (indent + var name + spaces + '=' + any spaces after)
        // match[2] = variable name (all caps)
        // match[3] = value part (including any spaces before a comment or line end)
        // match[4] = comment part (includes the '#' if present, or empty string if none)
        if (match) {
            const prefix = match[1];
            const varName = match[2];
            let valuePart = match[3] || ''; // this may include trailing spaces if any
            let commentPart = match[4] || ''; // includes '#' if present

            // Remove the leading '#' from commentPart for display, and trim whitespace
            let commentText = '';
            if (commentPart.startsWith('#')) {
                commentText = commentPart.replace(/^#\s*/, '');
            }
            // Trim value for editing, but preserve original spacing in prefix/postfix
            const trimmedValue = valuePart.trim();
            // Construct postfix as the comment portion (including the # and any trailing spaces/newline)
            const postfix = commentPart ? ` ${commentPart}` : '';
            vars.push({
                name: varName,
                value: trimmedValue,
                comment: commentText,
                lineIndex: idx,
                prefix: prefix, // includes indent, var name, '=' and spaces around it
                postfix: postfix // includes space + '#' and comment (if any)
            });
        }
    });
    return vars;
}

export interface MqttCredentials {
  mqtt_user: string;
  mqtt_password: string;
}

export interface SurveyDetails {
    surveyDate: string;
    surveyNumber: string;
    surveyorName: string;
    accreditationNumber: string;
    company: string;
    phone: string;
}

export interface ClientInfo {
    clientName: string;
    siteAddress: string;
    contactPhone: string;
    email: string;
    nmi: string;
    dnsp: string;
    retailer: string;
    propertyType: 'residential' | 'commercial' | 'rural' | 'strata' | '';
    ownerTenant: 'owner' | 'tenant' | '';
    landlordApproval: boolean;
    initialClientFeedback?: {
      preferredPVLocation: string;
      preferredEquipmentLocations: string;
      specialPlacementRequirements: string;
      securityConcerns: boolean | null;
      securityConcernsDetail: string;
      whsConcerns: boolean | null;
      whsConcernsDetail: string;
      otherComments: string;
    };
}

export interface Motivation {
    id: string;
    label: string;
    selected: boolean;
    priority: number | null;
}

export interface ClientMotivations {
    motivations: Motivation[];
    backupDuration: 'short' | 'medium' | 'extended' | '';
    essentialLoads: string;
    budgetMin: string;
    budgetMax: string;
    paybackPeriod: string;
    financeRequired: boolean | null;
}

export interface HazardItem {
    id: string;
    label: string;
    present: boolean | null;
    controlMeasure: string;
}

export interface RiskAssessment {
    siteAccessHazards: HazardItem[];
    electricalHazards: HazardItem[];
    batteryChemistry: 'lfp' | 'nmc' | 'lead-acid' | 'other' | '';
    batteryChemistryOther: string;
    fireRiskClassification: 'low' | 'medium' | 'high' | '';
    ventilationRequirements: 'natural' | 'forced' | 'na' | '';
    nonCombustibleBarrierRequired: boolean | null;
    take5: Take5Assessment;
}



export interface ExistingPVSystem {
    pvArrayUnknown: boolean;
    inverterDetailsUnknown: boolean;
    moduleManufacturer: string;
    moduleModel: string;
    moduleWattage: string;
    numberOfModules: string;
    totalArrayCapacity: string;
    arrayOrientation: string;
    arrayTilt: string;
    installationDate: string;
    cecApprovedModules: 'yes' | 'no' | 'unknown' | '';
    inverterManufacturer: string;
    inverterModel: string;
    inverterSerial: string;
    ratedACOutput: string;
    maxDCInput: string;
    numberOfMPPTs: string;
    inverterType: 'string' | 'micro' | 'hybrid' | '';
    cecApprovedInverter: 'yes' | 'no' | 'unknown' | '';
    as4777Compliant: '2020' | '2015' | 'no' | 'unknown' | '';
    inverterLocation: string;
    inverterCondition: 'good' | 'fair' | 'poor' | '';
    inverterConditionNotes: string;
    integrationOption: 'ac-coupled' | 'dc-coupled' | 'hybrid-ready' | '';
    hybridReadyModel: string;
    // Module electrical parameters — needed for string voltage calcs if replacing
    // inverter (DC-coupled integration) and for protection device sizing
    moduleVoc: string;           // Open circuit voltage at STC (V)
    moduleIsc: string;           // Short circuit current at STC (A)
    moduleVocTempCoeff: string;  // Temperature coefficient of Voc (%/°C) — for max voltage calc

    // String configuration — needed for array-inverter matching verification
    // and protection device decisions (System Protection module)
    modulesPerString: string;
    numberOfStrings: string;

    // DC optimisers — changes the voltage calculation method entirely
    // (Maximum Voltage module: partial DCU vs full DCU vs no DCU formulas)
    hasDCOptimisers: string;  // 'all' | 'partial' | 'none' | ''

    // Transformer topology — determines DVC classification of entire BESS
    // when battery connected. Non-separated (transformerless) inverter + DVC-A
    // battery = entire system classified DVC-C (GCBS 2025 Chapter 4)
    inverterTopology: string;  // 'non-separated' | 'separated-hf' | 'separated-lf' | 'unknown' | ''

    // MPPT voltage window — needed if keeping inverter (hybrid-ready or AC-coupled
    // with existing hybrid) to verify string config is within range
    mpptVoltageMin: string;   // V
    mpptVoltageMax: string;   // V

    // Max input current per MPPT — for verifying parallel string limits
    maxInputCurrentPerMPPT: string;  // A

    // DC isolator details — from System Protection module
    dcIsolatorPresent: boolean | null;
    dcIsolatorLocation: string;  // 'inverter-adjacent' | 'rooftop' | 'both' | ''

    roofSiteAssessment?: {
      weatherOnDay: string;
      compassOrientationCompleted: boolean | null;
      roofConstructionType: string;
      roofConstructionOther: string;
      roofAccessIdentified: boolean | null;
      roofAccessMethod: string;
      roofAccessDescription: string;
      roofHeightOver2m: boolean | null;
      roofHeightApprox: string;
      shadingObstructionsPresent: boolean | null;
      shadingObstructionsDetail: string;
      roofConditionNotes: string;
    };
}

export interface ElectricalInfrastructure {
    meterType: 'type4' | 'type5-6' | '';
    meterNumbers: string;
    currentTariffType: 'flat' | 'tou' | 'demand' | 'unknown' | '';
    supplyPhases: 'single' | 'three' | '';
    mainFuseRating: string;
    existingExportLimit: string;
    noExportLimit: boolean;
    zeroExport: boolean;
    distanceToTransformer: string;
    switchboardLocation: string;
    switchboardTypeAge: string;
    availableSpace: boolean | null;
    availablePositions: string;
    existingSolarACIsolator: boolean | null;
    existingSolarCBRating: string;
    existingSolarCBType: 'rcd' | 'mcb' | 'rcbo' | '';
    rcdProtection: 'all' | 'partial' | 'none' | '';
    mainSwitchPresent: boolean | null;
    switchboardCondition: 'good' | 'fair' | 'poor' | '';
    switchboardConditionNotes: string;
    labellingCompliant: 'yes' | 'no' | 'partial' | '';
    distanceBatteryToMSB: string;
    distanceBatteryToInverter: string;
    cableRouteObstacles: string[];
    conduitRequired: boolean | null;
    conduitLength: string;
    connectionPoleNumber: string;
    inverterConnectionPoint: string;  // 'msb' | 'db' | 'pv-sub-board' | ''
    existingSolarCBCurve: string;  // 'B' | 'C' | 'D' | ''
    rcdType: string;  // 'AC' | 'A' | 'F' | 'B' | 'unknown' | ''
    rcdDirectionSensitive: boolean | null;  // true = line/load sensitive, false = bidirectional
    consumerMainsCableSize: string;   // mm² e.g. '16', '25'
    consumerMainsInsulationType: string;  // 'tps' | 'xlpe' | 'pvc' | 'unknown' | ''
    consumerMainsRunLength: string;   // metres, from point of supply to MSB
    consumerMainsInstallMethod: string;  // 'unenclosed' | 'enclosed' | 'underground' | ''
}

export interface EnergyAssessment {
    averageDailyConsumption: string;
    averageDailyExport: string;
    peakDemand: string;
    annualImport: string;
    annualExport: string;
    selfConsumptionRate: string;
    eveningNightUsage: 'low' | 'medium' | 'high' | '';
    significantEveningLoads: string;
    hotWaterType: 'electric' | 'gas' | 'solar' | 'heat-pump' | '' ;
    hotWaterNotes: string;
}

export interface PhotoRecord {
  id: string;
  label: string;
  description?: string;
  taken: boolean;
  bucket?: string;
  storageKey?: string;
  uploadedAt?: string;
  isCustom?: boolean;
}

export interface RestrictedLocationChecks {
  notInHabitableRooms: boolean | null;
  notInPassageways: boolean | null;
  notUnderEntrances: boolean | null;
  notWithin600mmOfExit: boolean | null;
  notOnRoof: boolean | null;
  notInCeilingSpaces: boolean | null;
  notInWallCavities: boolean | null;
  // ERAC Section 3: Airflow and Vents — this is a separate restriction
  // from the general 600mm/900mm clearance zone. The clearance zone
  // (Section 7.3) is about appliances near the BESS. This check is about
  // the BESS itself being near openings to habitable rooms.
  notNearWindowsVents: boolean | null;
}

export interface BatteryLocation {
    primaryLocation: string;
    alternativeLocation: string;
    locationType: 'external-wall' | 'garage' | 'outbuilding' | 'other' | '';
    clientPreferenceDiscussed: boolean;
    clientPreferenceNotes: string;
    restrictedLocationChecks: RestrictedLocationChecks;
    clearance600mmSides: boolean | null;
    clearance600mmActual: string;
    clearance900mmAbove: boolean | null;
    clearance900mmActual: string;
    clearanceToCeiling: 'na' | 'yes' | 'no' | '';
    wallMaterial: 'brick' | 'concrete' | 'cement-sheet' | 'timber' | 'other' | '';
    wallMaterialOther: string;
    wallBacksOntoHabitable: boolean | null;
    nonCombustibleBarrierRequired: boolean | null;
    barrierWidth: string;
    barrierHeight: string;
    directSunlight: 'none' | 'morning' | 'afternoon' | 'all-day' | '';
    weatherExposure: 'fully-protected' | 'partial' | 'exposed' | '';
    estimatedMaxTemp: '<40' | '40-50' | '>50' | '';
    ipRatingRequired: 'ip55' | 'ip65' | 'other' | '';
    ipRatingOther: string;
    ventilation: 'good-natural' | 'limited' | 'forced-required' | '';
    floodRisk: 'low' | 'medium' | 'high' | '';
    mountingSurface: 'concrete-slab' | 'wall-mount' | 'frame-required' | '';
    estimatedBatteryWeight: string;
    surfaceCapable: 'yes' | 'no' | 'engineer-required' | '';

    // ERAC Section 1: External Damage
    vehicleDamageRisk: string;          // 'none' | 'yes-mitigated' | 'yes-unmitigated' | ''
    vehicleDamageMitigation: string;    // free text, shown when mitigated

    // ERAC Section 2: Hazardous Locations — Gas Cylinders
    // AS/NZS 3000:2018 Fig 4.18
    // Exchange cylinder: A=500mm, B=1500mm
    // In-situ fill:     A=1500mm, B=3500mm
    gasCylinderPresent: boolean | null;
    gasCylinderType: string;            // 'exchange' | 'in-situ' | ''
    gasCylinderDistance: string;         // measured distance in mm
    gasCylinderCompliant: boolean | null;

    // ERAC Section 2: Gas Relief Vent Terminal
    // AS/NZS 3000:2018 Fig 4.19
    gasReliefVentPresent: boolean | null;
    gasReliefVentDistance: string;       // mm

    // Gas meter — GNP-specific exclusion zones (ERAC note)
    gasMetrePresent: boolean | null;
    gasMetreDistance: string;            // mm

    // AS/NZS 5139 Clause 6.2.3.1
    nearCombustibleMaterials: boolean | null;
    combustibleMaterialNotes: string;
}

export interface VPPConsiderations {
    vppInterest: 'yes' | 'no' | 'undecided' | '';
    preferredVPPProvider: string;
    type4MeterPresent: boolean | null;
    internetConnection: 'wifi' | 'ethernet' | '4g-required' | 'none' | '';
    dnspExportConstraints: 'none' | 'fixed-limit' | 'dred-required' | '';
    dnspExportLimit: string;
    ctClampPossible: boolean | null;
    existingMonitoringPlatform: string;   // e.g. 'Solar Analytics', 'Fronius Solar.web', 'Tesla app', 'none', ''
    existingMonitoringAccess: boolean | null;  // customer has login/credentials available
    wifiSignalAtEquipment: string;  // 'strong' | 'weak' | 'none' | 'not-tested' | ''
    gridConnectionAgreementPresent: boolean | null;
}

export interface PhotoRecord {
    id: string;
    label: string;
    taken: boolean;
}


export interface BillUpload {
  id: string;
  label: string;
  fileType: 'pdf' | 'image' | '';
  bucket?: string;
  storageKey?: string;
  uploadedAt?: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  totalKwh?: string;
  peakKwh?: string;
  offPeakKwh?: string;
  shoulderKwh?: string;
  exportKwh?: string;
  dailySupplyCharge?: string;
  totalAmount?: string;
  notes?: string;
}

export interface ApplianceRecord {
  id: string;
  applianceType: 'air-conditioner' | 'pool-pump' | 'hot-water' | 'ev-charger' | 'oven' | 'dryer' | 'other' | '';
  applianceTypeOther?: string;
  brand?: string;
  model?: string;
  ratedPowerKw?: string;
  ratedCurrentAmps?: string;
  startingCurrentAmps?: string;
  voltage?: string;
  phases?: 'single' | 'three' | '';
  bucket?: string;
  storageKey?: string;
  uploadedAt?: string;
  typicalUsageHours?: string;
  usagePattern?: 'daytime' | 'evening' | 'overnight' | 'variable' | '';
  essentialForBackup?: boolean;
  notes?: string;
}


export interface SurveySummary {
    keyFindings: string;
    recommendedBatteryCapacity: string;
    integrationApproach: 'ac-coupled' | 'dc-coupled' | 'tbd' | '';
    switchboardUpgradeRequired: 'yes' | 'no' | 'tbd' | '';
    meterUpgradeRequired: 'yes' | 'no' | 'tbd' | '';
    dnspApprovalRequired: 'yes' | 'no' | '';
    designerSiteVisit: 'required' | 'not-required' | '';
    outstandingItems: string[];
    surveyorSignature: string;
    surveyorSignDate: string;
    clientSignature: string;
    clientSignDate: string;
    completionChecklist?: {
      clientOutcomeRecorded: boolean | null;
      walkroundCompleted: boolean | null;
      consumptionDataObtained: boolean | null;
      installStaffRoleExplained: boolean | null;
      equipmentLocationsConfirmed: boolean | null;
      nonNegotiableLocationsConfirmed: boolean | null;
      nonNegotiableLocationsDetail: string;
      findingsConfirmedWithClient: boolean | null;
    };

    cableLengths?: {
      pvToInverter: string;
      inverterToBattery: string;
      inverterToSwitchboard: string;
      switchboardToSupply: string;
      notes: string;
    };
    clientEscalations?: string[];

    installKit?: InstallKitItem[];
}

export interface InstallKitItem {
    id: string;
    label: string;
    quantity: number;
    unit: string;
    auto: boolean;        // true = auto-calculated from survey data
    notes: string;
}

export interface SurveyData {
    surveyDetails: SurveyDetails;
    clientInfo: ClientInfo;
    clientMotivations: ClientMotivations;
    riskAssessment: RiskAssessment;
    existingPVSystem: ExistingPVSystem;
    electricalInfrastructure: ElectricalInfrastructure;
    energyAssessment: EnergyAssessment;
    batteryLocation: BatteryLocation;
    vppConsiderations: VPPConsiderations;
    photoRecords: PhotoRecord[];
    billUploads: BillUpload[];
    applianceRecords: ApplianceRecord[];
    surveySummary: SurveySummary;
}

// --- EV chargers (car_chargers) -------------------------------------------
// One candidate command path detection has found for a charger.
// status: 'available' = transport known to answer; 'candidate' = seen in a
// scan but never exercised. See backend inverterintelligence/ev_transport.py.
export type EvDetectedTransport = {
  transport: 'ocpp_profile' | 'ocpp_start_stop' | 'modbus_direct' | 'modbus_relay' | 'none';
  params?: Record<string, unknown>;
  source?: string;
  status?: 'available' | 'candidate' | string;
  detected_at?: string;
};

export type EvCharger = {
  id: number;
  site_id: number;
  manufacturer?: string | null;
  product_name?: string | null;
  serial_number?: string | null;
  // Owner opt-in for Powston smart charging. False = Powston sends no
  // commands to this charger (vendor app / charger schedule stays in charge).
  control_enabled: boolean;
  // Pinned command path. null = auto (worker picks the preferred detected
  // transport); 'none' = commands explicitly disabled while keeping monitoring.
  control_transport: EvDetectedTransport['transport'] | null;
  transport_params?: Record<string, unknown> | null;
  detected_transports: EvDetectedTransport[];
  // Integrated chargers can be inventory/telemetry devices whose control
  // remains with another device (currently the site's primary inverter).
  settings_read_only?: boolean;
  control_owner?: 'primary_inverter' | string | null;
  created_at?: string | null;
  // Daily EV budget by kilometres (inverter-intelligence #1676): the car
  // picked from /api/ev_models, its consumption as stored on the row (copied
  // from the table at selection time, or typed for a car that is not
  // listed), the daily distance, and the kWh the backend derives from them
  // — null until the row can derive. See app/lib/evKmBudget.ts.
  ev_model_id?: string | null;
  ev_model?: string | null;
  kwh_per_100km?: number | null;
  // Per window (inverter-intelligence #1714): what the overnight window
  // should add and what the daytime top-up should add, each with the kWh
  // the backend derives from it. daily_km / daily_kwh_derived are their
  // sums, kept for one release; a listing that predates the split carries
  // only those.
  overnight_km?: number | null;
  daytime_km?: number | null;
  overnight_kwh_derived?: number | null;
  daytime_kwh_derived?: number | null;
  daily_km?: number | null;
  daily_kwh_derived?: number | null;
  ac_loss_factor?: number;
};

// One row of the vendored Open EV Data table served by GET /api/ev_models.
// The `attribution` on the response must be shown wherever the picker is —
// a condition of the dataset's licence.
export type EvModel = {
  id: string;
  brand: string;
  model: string;
  variant?: string | null;
  release_year?: number | null;
  kwh_per_100km: number;
  usable_battery_kwh?: number | null;
  ac_max_kw?: number | null;
};

export type EvModelsResponse = {
  attribution: string;
  licence?: string | null;
  source_updated?: string | null;
  count: number;
  ac_loss_factor: number;
  kwh_per_100km_range: [number, number];
  ev_models: EvModel[];
};

// Scheduled changes — future-dated settings changes an owner queues ahead of
// time (see the backend inverterintelligence/scheduled_changes.py). A `hold` is
// a temporary battery hold over a window that auto-reverts; a `tuning` change is
// a permanent parameter change that takes effect at a future time. `state` is
// the owner-facing lifecycle derived by the backend. Times are ISO strings that
// already carry the site-local offset.
export type ScheduledChangeKind = 'hold' | 'tuning';

export type ScheduledChangeState =
  | 'upcoming'
  | 'active'
  | 'finished'
  | 'applied'
  | 'failed'
  | 'cancelled';

export type ScheduledChange = {
  id: number;
  kind: ScheduledChangeKind;
  state: ScheduledChangeState;
  status: string;
  summary: string;
  changes: Record<string, unknown>;
  effective_at: string | null;
  expires_at: string | null;
  reason: string | null;
  notes: string | null;
  failure_note: string | null;
  created_at: string | null;
  applied_at: string | null;
};

export type ScheduledChangesResponse = {
  site_id: number;
  scheduled_changes: ScheduledChange[];
};
